/*
 * Walk N Wear product-card image auto-fit.
 *
 * Detects the actual footwear inside a white/transparent product image and
 * fits it into a consistent safe area. It runs again after Shopify filters,
 * sorting, pagination section renders, lazy loading, and viewport changes.
 */
(() => {
  const SELECTOR = '.product-card-wrapper .card__media .media img';
  const CACHE_PREFIX = 'wnw-card-subject-v6:';

  // Perceptual sizing: equal-width fitting makes slim running shoes look
  // smaller than wedges even when their measured widths match. Blend width
  // and height targets so every silhouette has comparable visual weight,
  // while hard caps prevent wide or tall footwear from touching the frame.
  const TARGET_WIDTH = 0.8;
  const TARGET_HEIGHT = 0.61;
  const MAX_SUBJECT_WIDTH = 0.86;
  const MAX_SUBJECT_HEIGHT = 0.72;
  const WIDTH_WEIGHT = 0.68;
  const HEIGHT_WEIGHT = 1 - WIDTH_WEIGHT;
  const TARGET_BOTTOM = 0.77;
  const MIN_SCALE = 0.6;
  const MAX_SCALE = 2.0;
  const MAX_SCAN_SIZE = 360;

  // A very small number of source images contain faint shadows/background
  // pixels that make the detected footwear silhouette look smaller or sit
  // too high. Keep these corrections product-scoped so every other card uses
  // the shared auto-fit calculation unchanged. The data attribute is rendered
  // by card-product.liquid, so this also survives Shopify facet/filter AJAX.
  const PRODUCT_ADJUSTMENTS = Object.freeze({
    'nike-mens-flex-2017-rn-black-running-shoes': Object.freeze({
      scaleMultiplier: 1.08,
      targetBottom: 0.83,
    }),
  });

  const resultCache = new Map();
  const pendingScans = new Map();
  const trackedImages = new Set();
  let resizeFrame = 0;

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function median(values) {
    const sorted = values.sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)] || 255;
  }

  function cacheKey(image) {
    try {
      const url = new URL(image.currentSrc || image.src, window.location.href);
      url.searchParams.delete('width');
      return url.toString();
    } catch (error) {
      return image.currentSrc || image.src;
    }
  }

  function readStoredResult(key) {
    try {
      const value = window.sessionStorage.getItem(CACHE_PREFIX + key);
      if (!value) return null;
      const parsed = JSON.parse(value);
      if (
        typeof parsed.left === 'number' &&
        typeof parsed.top === 'number' &&
        typeof parsed.right === 'number' &&
        typeof parsed.bottom === 'number'
      ) {
        return parsed;
      }
    } catch (error) {
      // Storage may be unavailable in privacy modes; memory cache still works.
    }
    return null;
  }

  function storeResult(key, result) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(result));
    } catch (error) {
      // A failed cache write must never prevent card rendering.
    }
  }

  function detectSubject(image) {
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    if (!naturalWidth || !naturalHeight) return null;

    const scanScale = Math.min(1, MAX_SCAN_SIZE / Math.max(naturalWidth, naturalHeight));
    const width = Math.max(1, Math.round(naturalWidth * scanScale));
    const height = Math.max(1, Math.round(naturalHeight * scanScale));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    let pixelData;
    try {
      pixelData = context.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null;
    }

    const patch = Math.max(3, Math.round(Math.min(width, height) * 0.025));
    const redSamples = [];
    const greenSamples = [];
    const blueSamples = [];
    const alphaSamples = [];
    const corners = [
      [0, 0],
      [width - patch, 0],
      [0, height - patch],
      [width - patch, height - patch],
    ];

    corners.forEach(([startX, startY]) => {
      for (let y = startY; y < startY + patch; y += 1) {
        for (let x = startX; x < startX + patch; x += 1) {
          const index = (y * width + x) * 4;
          redSamples.push(pixelData[index]);
          greenSamples.push(pixelData[index + 1]);
          blueSamples.push(pixelData[index + 2]);
          alphaSamples.push(pixelData[index + 3]);
        }
      }
    });

    const backgroundRed = median(redSamples);
    const backgroundGreen = median(greenSamples);
    const backgroundBlue = median(blueSamples);
    const transparentBackground = median(alphaSamples) < 245;
    const rowCounts = new Uint32Array(height);
    const columnCounts = new Uint32Array(width);

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const red = pixelData[index];
        const green = pixelData[index + 1];
        const blue = pixelData[index + 2];
        const alpha = pixelData[index + 3];
        let isForeground = false;

        if (transparentBackground) {
          isForeground = alpha > 24;
        } else if (alpha > 18) {
          if (alpha < 245) {
            isForeground = true;
          } else {
            const redDifference = red - backgroundRed;
            const greenDifference = green - backgroundGreen;
            const blueDifference = blue - backgroundBlue;
            const distance = Math.sqrt(
              redDifference * redDifference +
                greenDifference * greenDifference +
                blueDifference * blueDifference
            );
            const maximumChannel = Math.max(red, green, blue);
            const chroma = maximumChannel - Math.min(red, green, blue);
            isForeground = distance > 20 || (chroma > 10 && maximumChannel < 252);
          }
        }

        if (isForeground) {
          rowCounts[y] += 1;
          columnCounts[x] += 1;
        }
      }
    }

    const minimumRowPixels = Math.max(2, Math.round(width * 0.006));
    const minimumColumnPixels = Math.max(2, Math.round(height * 0.006));
    let left = 0;
    let right = width - 1;
    let top = 0;
    let bottom = height - 1;

    while (left < width && columnCounts[left] < minimumColumnPixels) left += 1;
    while (right >= 0 && columnCounts[right] < minimumColumnPixels) right -= 1;
    while (top < height && rowCounts[top] < minimumRowPixels) top += 1;
    while (bottom >= 0 && rowCounts[bottom] < minimumRowPixels) bottom -= 1;

    if (left >= right || top >= bottom) return null;

    const detectedWidth = right - left + 1;
    const detectedHeight = bottom - top + 1;
    if (detectedWidth < width * 0.08 || detectedHeight < height * 0.04) return null;

    return {
      left: left / width,
      top: top / height,
      right: (right + 1) / width,
      bottom: (bottom + 1) / height,
    };
  }

  function subjectResult(image) {
    const key = cacheKey(image);
    if (resultCache.has(key)) return Promise.resolve(resultCache.get(key));

    const stored = readStoredResult(key);
    if (stored) {
      resultCache.set(key, stored);
      return Promise.resolve(stored);
    }

    if (pendingScans.has(key)) return pendingScans.get(key);

    const pending = Promise.resolve()
      .then(() => detectSubject(image))
      .then((result) => {
        if (result) {
          resultCache.set(key, result);
          storeResult(key, result);
        }
        pendingScans.delete(key);
        return result;
      })
      .catch(() => {
        pendingScans.delete(key);
        return null;
      });

    pendingScans.set(key, pending);
    return pending;
  }

  function applyFit(image, subject) {
    if (!image.isConnected || !subject) return;
    const frame = image.closest('.media');
    if (!frame) return;

    const frameWidth = frame.clientWidth;
    const frameHeight = frame.clientHeight;
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    if (!frameWidth || !frameHeight || !naturalWidth || !naturalHeight) return;

    const containedScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight);
    const containedWidth = naturalWidth * containedScale;
    const containedHeight = naturalHeight * containedScale;
    const containedLeft = (frameWidth - containedWidth) / 2;
    const containedTop = (frameHeight - containedHeight) / 2;
    const subjectLeft = containedLeft + subject.left * containedWidth;
    const subjectTop = containedTop + subject.top * containedHeight;
    const subjectRight = containedLeft + subject.right * containedWidth;
    const subjectBottom = containedTop + subject.bottom * containedHeight;
    const subjectWidth = subjectRight - subjectLeft;
    const subjectHeight = subjectBottom - subjectTop;
    if (subjectWidth <= 0 || subjectHeight <= 0) return;

    const widthFit = (frameWidth * TARGET_WIDTH) / subjectWidth;
    const heightFit = (frameHeight * TARGET_HEIGHT) / subjectHeight;
    const perceptualFit =
      Math.pow(widthFit, WIDTH_WEIGHT) * Math.pow(heightFit, HEIGHT_WEIGHT);
    const widthCap = (frameWidth * MAX_SUBJECT_WIDTH) / subjectWidth;
    const heightCap = (frameHeight * MAX_SUBJECT_HEIGHT) / subjectHeight;
    const baseScale = clamp(
      Math.min(perceptualFit, widthCap, heightCap),
      MIN_SCALE,
      MAX_SCALE
    );
    const productHandle = image.closest('[data-wnw-product-handle]')?.dataset
      .wnwProductHandle;
    const adjustment = PRODUCT_ADJUSTMENTS[productHandle] || {};
    const scale = clamp(
      baseScale * (adjustment.scaleMultiplier || 1),
      MIN_SCALE,
      MAX_SCALE
    );
    const targetBottom = adjustment.targetBottom || TARGET_BOTTOM;
    const subjectCenterX = (subjectLeft + subjectRight) / 2;
    const translateX = frameWidth / 2 - scale * subjectCenterX;
    const translateY = frameHeight * targetBottom - scale * subjectBottom;

    image.style.setProperty(
      '--wnw-card-image-autofit',
      `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`
    );
    image.style.setProperty('--wnw-card-image-autofit-origin', 'top left');
    image.dataset.wnwAutofit = 'ready';
    image._wnwSubject = subject;
  }

  function processImage(image) {
    if (!(image instanceof HTMLImageElement)) return;
    trackedImages.add(image);

    const run = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      subjectResult(image).then((subject) => applyFit(image, subject));
    };

    if (image.complete && image.naturalWidth) {
      run();
    } else if (image.dataset.wnwAutofitWaiting !== 'true') {
      image.dataset.wnwAutofitWaiting = 'true';
      image.addEventListener(
        'load',
        () => {
          delete image.dataset.wnwAutofitWaiting;
          run();
        },
        { once: true }
      );
    }
  }

  function processRoot(root = document) {
    if (root instanceof Element && root.matches(SELECTOR)) processImage(root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll(SELECTOR).forEach(processImage);
  }

  function refitTrackedImages() {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      trackedImages.forEach((image) => {
        if (!image.isConnected) {
          trackedImages.delete(image);
        } else if (image._wnwSubject) {
          applyFit(image, image._wnwSubject);
        } else {
          processImage(image);
        }
      });
    });
  }

  function start() {
    processRoot(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) processRoot(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', refitTrackedImages, { passive: true });
    document.addEventListener('shopify:section:load', (event) => processRoot(event.target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
